import React, { useState, useEffect, useCallback } from 'react';
import { ModernButton } from '../ui/ModernButton';
import { SplitButton } from '../ui/SplitButton';
import { ArrowLeftIcon, UserGroupIcon } from '../ui/Icons';
import campaignService from '../../services/CampaignService';
import { ApplicantDetailPage } from './ApplicantDetailPage';
import { CreateCampaignPage } from './CreateCampaignPage';
import { AddApplicantPage } from './AddApplicantPage';
import { FragebogenCreationPage } from './FragebogenCreationPage';
import { FragebogenTakerPage } from './FragebogenTakerPage';
import { CustomRouteModal } from '../modals/CustomRouteModal';
import fragebogenService from '../../services/FragebogenService';
import { getFragebogenUrl, getLandingPageUrl } from '../Router';
import { collectBrowserMetadata } from '../../utils/browserMetadata.js';
import { normalizeQuestionnaireData } from '../../utils/questionnairePages';

// --- Notification Component ---
function Notification({ message, type = 'success', onClose }) {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose();
        }, 3000); // Auto-hide after 3 seconds

        return () => clearTimeout(timer);
    }, [onClose]);

    return (
        <div className="fixed top-4 right-4 z-50 transform transition-all duration-300 ease-out animate-pulse">
            <div className="bg-white dark:bg-slate-800 border border-green-200 dark:border-green-700 rounded-lg shadow-lg p-4 flex items-center gap-3 min-w-[200px]">
                <div className="shrink-0">
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{message}</p>
                </div>
                <button
                    onClick={onClose}
                    className="shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>
        </div>
    );
}

// Status color mapping
const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
        case 'unbearbeitet':
            return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700';
        case 'in kontakt':
            return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700';
        case 'absage':
            return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700';
        case 'zusage':
            return 'bg-green-100 text-green-800 border-green-400 dark:bg-green-900/30 dark:text-green-300 dark:border-green-400';
        default:
            return 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600';
    }
};

// Status icon mapping
const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
        case 'unbearbeitet':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        case 'in kontakt':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
            );
        case 'absage':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            );
        case 'zusage':
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
            );
        default:
            return (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
    }
};

// Status badge component
function StatusBadge({ status, onClick }) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border transition-colors hover:opacity-80 min-w-[100px] justify-center ${getStatusColor(status)}`}
        >
            {getStatusIcon(status)}
            {status || 'Unbekannt'}
        </button>
    );
}

// Neu badge component
function NeuBadge() {
    return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-700">
            Neu
        </span>
    );
}

// Main Bewerbungspool Page
export function BewerbungspoolPage({ navigateTo }) {
    const [campaigns, setCampaigns] = useState([]);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [applicants, setApplicants] = useState([]);
    const [filteredApplicants, setFilteredApplicants] = useState([]);
    const [selectedApplicant, setSelectedApplicant] = useState(null);
    const [showDetailPage, setShowDetailPage] = useState(false);
    const [selectedApplicants, setSelectedApplicants] = useState(new Set());
    const [viewedApplicants, setViewedApplicants] = useState(() => {
        // Load viewed applicants from localStorage on initialization
        try {
            const stored = localStorage.getItem('viewedApplicants');
            return stored ? new Set(JSON.parse(stored)) : new Set();
        } catch (error) {
            console.error('Error loading viewed applicants from localStorage:', error);
            return new Set();
        }
    });
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('Alle');
    const [sortField, setSortField] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');
    const [showCreateCampaign, setShowCreateCampaign] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [showAddApplicant, setShowAddApplicant] = useState(false);
    const [showFragebogenCreation, setShowFragebogenCreation] = useState(false);
    const [showFragebogenTaker, setShowFragebogenTaker] = useState(false);
    const [showCustomRouteModal, setShowCustomRouteModal] = useState(false);
    const [showQrModal, setShowQrModal] = useState(false);
    const [qrModalUrl, setQrModalUrl] = useState('');
    const [qrDownloading, setQrDownloading] = useState(false);
    const [currentFragebogen, setCurrentFragebogen] = useState(null);
    const [notification, setNotification] = useState(null);

    // Helper function to check if an applicant is "new"
    const isNewApplicant = (applicant) => {
        // If already viewed, not new
        if (viewedApplicants.has(applicant.leadID)) {
            return false;
        }
        
        // If createdAt and lastChange are the same, it's new (no modifications)
        const createdAt = new Date(applicant.createdAt);
        const lastChange = new Date(applicant.lastChange);
        
        // Check if the times are within 1 second of each other (essentially the same)
        const timeDiff = Math.abs(lastChange.getTime() - createdAt.getTime());
        return timeDiff < 1000; // Less than 1 second difference
    };

    // Track when an applicant profile is clicked
    const handleApplicantClick = (applicant) => {
        // Mark as viewed
        const newViewedApplicants = new Set([...viewedApplicants, applicant.leadID]);
        setViewedApplicants(newViewedApplicants);
        
        // Save to localStorage
        try {
            localStorage.setItem('viewedApplicants', JSON.stringify([...newViewedApplicants]));
        } catch (error) {
            console.error('Error saving viewed applicants to localStorage:', error);
        }
        
        // Open detail page
        setSelectedApplicant(applicant);
        setShowDetailPage(true);
        
        // Ensure we have the correct campaign selected
        if (!selectedCampaign || !selectedCampaign.applicants?.some(app => app.leadID === applicant.leadID)) {
            // Find the campaign that contains this applicant
            const campaignWithApplicant = campaigns.find(campaign => 
                campaign.applicants?.some(app => app.leadID === applicant.leadID)
            );
            if (campaignWithApplicant) {
                setSelectedCampaign(campaignWithApplicant);
            }
        }
    };

    // Show notification function
    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
    };

    // Load campaigns and applicants from PocketBase
    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            
            // Try to load from PocketBase first
            const result = await campaignService.getCampaigns();
            
            if (result.success && result.data.length > 0) {
                // Convert PocketBase data to our format
                const campaigns = result.data.map(campaign => ({
                    id: campaign.id,
                    name: campaign.name,
                    applicantCount: (campaign.applicants || []).length,
                    color: campaign.color,
                    status: campaign.status,
                    enable_quiz_scoring: campaign.enable_quiz_scoring === true,
                    landingPage: campaign.landing_page_data,
                    questionnaire_data: campaign.questionnaire_data,
                    customRoute: campaign.company_name ? {
                        companyName: campaign.company_name,
                        campaignName: campaign.campaign_name,
                        landingPageName: campaign.landing_page_name
                    } : null,
                    applicants: campaign.applicants || []
                }));
                
                setCampaigns(campaigns);
                
                // Set first campaign as selected by default
                if (campaigns.length > 0 && !selectedCampaign) {
                    setSelectedCampaign(campaigns[0]);
                    setApplicants(campaigns[0].applicants || []);
                }
                
                return;
            }
            
            // No campaigns found in PocketBase
            setCampaigns([]);
            setSelectedCampaign(null);
            setApplicants([]);
            setFilteredApplicants([]);
        } catch (error) {
            console.error('Error loading data:', error);
            setCampaigns([]);
            setApplicants([]);
            setFilteredApplicants([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedCampaign]);

    // Filter and sort applicants
    useEffect(() => {
        let filtered = applicants.filter(applicant => {
            const matchesSearch = !searchTerm || 
                applicant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                applicant.email?.toLowerCase().includes(searchTerm.toLowerCase());
            
            const matchesStatus = statusFilter === 'Alle' || applicant.status === statusFilter;
            
            return matchesSearch && matchesStatus;
        });

        // Sort applicants
        filtered.sort((a, b) => {
            let aValue, bValue;
            
            switch (sortField) {
                case 'name':
                    aValue = a.name || '';
                    bValue = b.name || '';
                    break;
                case 'status':
                    aValue = a.status || '';
                    bValue = b.status || '';
                    break;
                case 'date':
                default:
                    aValue = new Date(a.createdAt);
                    bValue = new Date(b.createdAt);
                    break;
            }

            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        setFilteredApplicants(filtered);
    }, [applicants, searchTerm, statusFilter, sortField, sortDirection]);

    // Handle campaign selection
    const handleCampaignSelect = (campaign) => {
        setSelectedCampaign(campaign);
        setApplicants(campaign.applicants);
        setFilteredApplicants(campaign.applicants);
        setSelectedApplicants(new Set()); // Clear selections when switching campaigns
    };

    // Handle campaign creation
    const handleCreateCampaign = async (campaign) => {
        try {
            // Convert to PocketBase format
            const campaignData = {
                name: campaign.name,
                color: campaign.color,
                status: campaign.status || "online",
                enable_quiz_scoring: campaign.enable_quiz_scoring === true,
                company_name: null,
                campaign_name: null,
                landing_page_name: null,
                landing_page_data: null,
                applicants: [],
                questionnaire_data: null
            };
            
            const result = await campaignService.createCampaign(campaignData);
            
            if (result.success) {
                // Convert back to our format and add to state
                const newCampaign = {
                    id: result.data.id,
                    name: result.data.name,
                    applicantCount: 0,
                    color: result.data.color,
                    status: result.data.status,
                    enable_quiz_scoring: result.data.enable_quiz_scoring === true,
                    landingPage: result.data.landing_page_data,
                    customRoute: null,
                    applicants: []
                };
                
                setCampaigns(prev => [...prev, newCampaign]);
                setShowCreateCampaign(false);
            } else {
                console.error('Failed to create campaign:', result.error);
                alert('Fehler beim Erstellen der Kampagne: ' + result.error);
            }
        } catch (error) {
            console.error('Error creating campaign:', error);
            alert('Fehler beim Erstellen der Kampagne');
        }
    };

    // Handle campaign deletion
    const handleDeleteCampaign = (campaignId) => {
        const campaign = campaigns.find(c => c.id === campaignId);
        setCampaignToDelete(campaign);
        setShowDeleteConfirm(true);
    };

    const confirmDeleteCampaign = async () => {
        if (campaignToDelete) {
            try {
                const result = await campaignService.deleteCampaign(campaignToDelete.id);
                
                if (result.success) {
                    setCampaigns(prev => prev.filter(c => c.id !== campaignToDelete.id));
                    // If the deleted campaign was selected, select the first available campaign
                    if (selectedCampaign?.id === campaignToDelete.id) {
                        const remainingCampaigns = campaigns.filter(c => c.id !== campaignToDelete.id);
                        if (remainingCampaigns.length > 0) {
                            handleCampaignSelect(remainingCampaigns[0]);
                        } else {
                            setSelectedCampaign(null);
                            setApplicants([]);
                            setFilteredApplicants([]);
                        }
                    }
                } else {
                    console.error('Failed to delete campaign:', result.error);
                    alert('Fehler beim Löschen der Kampagne: ' + result.error);
                }
            } catch (error) {
                console.error('Error deleting campaign:', error);
                alert('Fehler beim Löschen der Kampagne');
            }
        }
        setShowDeleteConfirm(false);
        setCampaignToDelete(null);
    };

    const cancelDeleteCampaign = () => {
        setShowDeleteConfirm(false);
        setCampaignToDelete(null);
    };

    // Handle adding new applicant
    const handleAddApplicant = async (newApplicant) => {
        if (selectedCampaign) {
            try {
                // Save applicant to PocketBase
                const result = await campaignService.addApplicantToCampaign(selectedCampaign.id, newApplicant);
                
                if (result.success) {
                    // Update local state
                    setCampaigns(prev => prev.map(campaign => {
                        if (campaign.id === selectedCampaign.id) {
                            const updatedCampaign = {
                                ...campaign,
                                applicants: [...campaign.applicants, newApplicant],
                                applicantCount: campaign.applicantCount + 1
                            };
                            setSelectedCampaign(updatedCampaign);
                            setApplicants(updatedCampaign.applicants);
                            setFilteredApplicants(updatedCampaign.applicants);
                            return updatedCampaign;
                        }
                        return campaign;
                    }));
                    console.log('Applicant added successfully');
                } else {
                    console.error('Failed to add applicant:', result.error);
                    alert('Fehler beim Hinzufügen des Bewerbers: ' + result.error);
                }
            } catch (error) {
                console.error('Error adding applicant:', error);
                alert('Fehler beim Hinzufügen des Bewerbers');
            }
        }
        setShowAddApplicant(false);
    };

    // Handle updating existing applicant
    const handleApplicantUpdate = (updatedApplicant) => {
        
        // Update campaigns state
        setCampaigns(prev => prev.map(campaign => {
            if (campaign.id === selectedCampaign?.id) {
                const updatedApplicants = campaign.applicants.map(applicant => 
                    applicant.leadID === updatedApplicant.leadID ? updatedApplicant : applicant
                );
                const updatedCampaign = {
                    ...campaign,
                    applicants: updatedApplicants
                };
                
                // Update selectedCampaign if it's the current one
                if (selectedCampaign?.id === campaign.id) {
                    setSelectedCampaign(updatedCampaign);
                    setApplicants(updatedApplicants);
                    setFilteredApplicants(updatedApplicants);
                }
                
                return updatedCampaign;
            }
            return campaign;
        }));
    };

    // Handle landing page editor
    const handleEditLandingPage = () => {
        if (selectedCampaign) {
            // Check if campaign already has a landing page and route data
            if (selectedCampaign.landingPage && selectedCampaign.customRoute) {
                // Landing page already exists, navigate to editor route
                const { companyName, campaignName, landingPageName } = selectedCampaign.customRoute;
                window.location.href = `/${companyName}/${campaignName}/${landingPageName}/edit`;
            } else {
                // No landing page yet, show route modal first
                setShowCustomRouteModal(true);
            }
        }
    };

    // Handle custom route confirmation
    const handleCustomRouteConfirm = async (routeData) => {
        try {
            // Always save the route to the database for the selected campaign
            if (selectedCampaign) {
                const result = await campaignService.updateCampaign(selectedCampaign.id, {
                    company_name: routeData.companyName,
                    campaign_name: routeData.campaignName,
                    landing_page_name: routeData.landingPageName
                });
                
                if (result.success) {
                    // Update local state
                    setCampaigns(prev => prev.map(campaign => {
                        if (campaign.id === selectedCampaign.id) {
                            const updatedCampaign = {
                                ...campaign,
                                customRoute: routeData
                            };
                            setSelectedCampaign(updatedCampaign);
                            return updatedCampaign;
                        }
                        return campaign;
                    }));
                    console.log('Route saved successfully');
                } else {
                    console.error('Failed to save route:', result.error);
                    alert('Fehler beim Speichern der Route: ' + result.error);
                    return;
                }
            }
            
            setShowCustomRouteModal(false);
            // Navigate to the editor route
            const { companyName, campaignName, landingPageName } = routeData;
            window.location.href = `/${companyName}/${campaignName}/${landingPageName}/edit`;
        } catch (error) {
            console.error('Error saving route:', error);
            alert('Fehler beim Speichern der Route');
        }
    };


    // Get all existing routes for duplicate checking
    const getExistingRoutes = () => {
        return campaigns
            .filter(campaign => campaign.customRoute)
            .map(campaign => `/${campaign.customRoute.companyName}/${campaign.customRoute.campaignName}/${campaign.customRoute.landingPageName}`);
    };

    // Handle viewing landing page
    const handleViewLandingPage = () => {
        if (selectedCampaign && selectedCampaign.customRoute) {
            // Use custom route data with karrieresichern.de base URL
            const { companyName, campaignName, landingPageName } = selectedCampaign.customRoute;
            const url = `https://karrieresichern.de/${companyName}/${campaignName}/${landingPageName}`;
            window.open(url, '_blank');
        }
    };


    // Handle fragebogen creation
    const handleCreateFragebogen = () => {
        if (selectedCampaign) {
            setShowFragebogenCreation(true);
        }
    };

    // Handle fragebogen saving
    const handleSaveFragebogen = async (fragebogenData) => {
        if (selectedCampaign) {
            try {
                const normalizedFragebogen = normalizeQuestionnaireData(fragebogenData);
                const result = fragebogenService.saveFragebogen(selectedCampaign.id, normalizedFragebogen);
                if (result.success) {
                    // Update campaign in PocketBase
                    await campaignService.updateCampaign(selectedCampaign.id, {
                        questionnaire_data: normalizedFragebogen
                    });
                    
                    // Update local state
                    setCampaigns(prev => prev.map(campaign => {
                        if (campaign.id === selectedCampaign.id) {
                            const updatedCampaign = {
                                ...campaign,
                                questionnaire_data: normalizedFragebogen
                            };
                            setSelectedCampaign(updatedCampaign);
                            return updatedCampaign;
                        }
                        return campaign;
                    }));
                    
                    showNotification('Quiz gespeichert!');
                    // Don't close the fragebogen creation page - let user continue editing
                } else {
                    alert('Fehler beim Speichern des Quiz: ' + result.error);
                }
            } catch (error) {
                console.error('Error saving fragebogen:', error);
                alert('Fehler beim Speichern des Quiz');
            }
        }
    };

    // Handle fragebogen taking
    const handleTakeFragebogen = () => {
        if (selectedCampaign) {
            const fragebogenResult = fragebogenService.getFragebogen(selectedCampaign.id);
            if (fragebogenResult.success && fragebogenResult.data) {
                setCurrentFragebogen(fragebogenResult.data);
                setShowFragebogenTaker(true);
            } else {
                alert('Kein Quiz für diese Kampagne gefunden.');
            }
        }
    };

    // Handle fragebogen submission
    const handleFragebogenSubmission = async (submission) => {
        if (selectedCampaign) {
            try {
                // Collect browser metadata
                const browserMetadata = collectBrowserMetadata();
                
                // Create applicant data with fragebogen responses
                const normalizedQuestionnaire = normalizeQuestionnaireData(selectedCampaign.questionnaire_data);
                const applicantData = {
                    leadID: `lead_${Date.now()}`,
                    name: submission.contactInfo.name,
                    email: submission.contactInfo.email,
                    phone: submission.contactInfo.phone || '',
                    status: 'Unbearbeitet',
                    createdAt: new Date().toISOString(),
                    lastChange: new Date().toISOString(),
                    notes: '',
                    experience: '',
                    skills: '',
                    rawFunnelData: [],
                    meta_data: browserMetadata,
                    // Store fragebogen responses as question-answer pairs
                    fragebogenResponses: Object.entries(submission.answers).map(([questionId, answer]) => {
                        const question = normalizedQuestionnaire?.questions?.find(q => String(q.id) === String(questionId));
                        return {
                            questionId,
                            questionText: question?.text || '',
                            questionType: question?.type || '',
                            answer: answer,
                            answeredAt: new Date().toISOString()
                        };
                    })
                };

                // Add applicant to campaign in PocketBase
                const result = await campaignService.addApplicantToCampaign(selectedCampaign.id, applicantData);
                
                if (result.success) {
                    // Update local state
                    setCampaigns(prev => prev.map(campaign => {
                        if (campaign.id === selectedCampaign.id) {
                            const updatedCampaign = {
                                ...campaign,
                                applicants: [...campaign.applicants, applicantData],
                                applicantCount: campaign.applicantCount + 1
                            };
                            setSelectedCampaign(updatedCampaign);
                            setApplicants(updatedCampaign.applicants);
                            setFilteredApplicants(updatedCampaign.applicants);
                            return updatedCampaign;
                        }
                        return campaign;
                    }));
                    
                    alert('Bewerbung erfolgreich eingereicht!');
                    setShowFragebogenTaker(false);
                    setCurrentFragebogen(null);
                } else {
                    alert('Fehler beim Einreichen der Bewerbung: ' + result.error);
                }
            } catch (error) {
                console.error('Error submitting fragebogen:', error);
                alert('Fehler beim Einreichen der Bewerbung');
            }
        }
    };

    // Handle campaign status change
    const handleStatusChange = async (newStatus) => {
        if (selectedCampaign) {
            try {
                // Update status in PocketBase
                const statusData = {
                    status: newStatus.toLowerCase() // Ensure lowercase as per requirements
                };
                
                console.log('Updating campaign status:', {
                    campaignId: selectedCampaign.id,
                    campaignName: selectedCampaign.name,
                    newStatus: statusData.status
                });
                
                const result = await campaignService.updateCampaign(selectedCampaign.id, statusData);
                
                if (result.success) {
                    // Update local state only after successful PocketBase update
                    setCampaigns(prev => prev.map(campaign => {
                        if (campaign.id === selectedCampaign.id) {
                            const updatedCampaign = {
                                ...campaign,
                                status: newStatus.toLowerCase()
                            };
                            setSelectedCampaign(updatedCampaign);
                            return updatedCampaign;
                        }
                        return campaign;
                    }));
                    
                    console.log(`Campaign status updated to: ${newStatus}`);
                } else {
                    console.error('Failed to update campaign status:', result.error);
                    alert('Fehler beim Aktualisieren des Kampagnenstatus: ' + result.error);
                }
            } catch (error) {
                console.error('Error updating campaign status:', error);
                alert('Fehler beim Aktualisieren des Kampagnenstatus');
            }
        }
    };

    const handleQuizScoringToggle = async (enabled) => {
        if (selectedCampaign) {
            try {
                const result = await campaignService.updateCampaign(selectedCampaign.id, {
                    enable_quiz_scoring: enabled
                });

                if (result.success) {
                    setCampaigns(prev => prev.map(campaign => {
                        if (campaign.id === selectedCampaign.id) {
                            const updatedCampaign = {
                                ...campaign,
                                enable_quiz_scoring: enabled
                            };
                            setSelectedCampaign(updatedCampaign);
                            return updatedCampaign;
                        }
                        return campaign;
                    }));
                } else {
                    console.error('Failed to update quiz scoring:', result.error);
                    alert('Fehler beim Aktualisieren der Quiz-Bewertung: ' + result.error);
                }
            } catch (error) {
                console.error('Error updating quiz scoring:', error);
                alert('Fehler beim Aktualisieren der Quiz-Bewertung');
            }
        }
    };

    useEffect(() => {
        loadData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleStatusUpdate = async (leadID, newStatus) => {
        try {
            if (selectedCampaign) {
                // Update status in PocketBase
                const result = await campaignService.updateApplicantInCampaign(
                    selectedCampaign.id, 
                    leadID, 
                    { status: newStatus, lastChange: new Date().toISOString() }
                );
                
                if (result.success) {
                    // Update local state
                    setApplicants(prev => prev.map(applicant => 
                        applicant.leadID === leadID 
                            ? { ...applicant, status: newStatus, lastChange: new Date().toISOString() }
                            : applicant
                    ));
                    
                    if (selectedApplicant?.leadID === leadID) {
                        setSelectedApplicant(prev => ({ ...prev, status: newStatus }));
                    }
                    
                    // Update campaigns state
                    setCampaigns(prev => prev.map(campaign => {
                        if (campaign.id === selectedCampaign.id) {
                            return {
                                ...campaign,
                                applicants: campaign.applicants.map(applicant =>
                                    applicant.leadID === leadID 
                                        ? { ...applicant, status: newStatus, lastChange: new Date().toISOString() }
                                        : applicant
                                )
                            };
                        }
                        return campaign;
                    }));
                    
                    console.log('Applicant status updated successfully');
                } else {
                    console.error('Failed to update applicant status:', result.error);
                    alert('Fehler beim Aktualisieren des Status: ' + result.error);
                }
            }
        } catch (error) {
            console.error('Error updating applicant status:', error);
            alert('Fehler beim Aktualisieren des Status');
        }
    };

    // Bulk selection functions
    const handleSelectAll = (checked) => {
        if (checked) {
            setSelectedApplicants(new Set(filteredApplicants.map(app => app.leadID)));
        } else {
            setSelectedApplicants(new Set());
        }
    };

    const handleSelectApplicant = (leadID, checked) => {
        const newSelected = new Set(selectedApplicants);
        if (checked) {
            newSelected.add(leadID);
        } else {
            newSelected.delete(leadID);
        }
        setSelectedApplicants(newSelected);
    };

    const handleBulkDelete = async () => {
        if (selectedApplicants.size === 0) return;
        
        const confirmed = window.confirm(`Möchten Sie ${selectedApplicants.size} Bewerber wirklich löschen?`);
        if (!confirmed) return;

        try {
            // Delete from campaign
            for (const leadID of selectedApplicants) {
                const result = await campaignService.deleteApplicantFromCampaign(selectedCampaign.id, leadID);
                if (!result.success) {
                    throw new Error(result.error || 'Fehler beim Löschen des Bewerbers');
                }
            }
            
            // Update local state
            setApplicants(prev => prev.filter(app => !selectedApplicants.has(app.leadID)));
            setFilteredApplicants(prev => prev.filter(app => !selectedApplicants.has(app.leadID)));
            setSelectedApplicants(new Set());
            
            // Update campaigns state
            setCampaigns(prev => prev.map(campaign => {
                if (campaign.id === selectedCampaign.id) {
                    return {
                        ...campaign,
                        applicants: campaign.applicants.filter(app => !selectedApplicants.has(app.leadID))
                    };
                }
                return campaign;
            }));
        } catch (error) {
            console.error('Error deleting applicants:', error);
            alert('Fehler beim Löschen der Bewerber: ' + error.message);
        }
    };

    const statusOptions = ['Alle', 'Unbearbeitet', 'In Kontakt', 'Absage', 'Zusage'];

    // If showing detail page, render only the detail page
    if (showDetailPage && selectedApplicant) {
    return (
            <ApplicantDetailPage
                applicant={selectedApplicant}
                questionnaireData={selectedCampaign?.questionnaire_data}
                campaignId={selectedCampaign?.id}
                onBack={() => {
                    setShowDetailPage(false);
                    setSelectedApplicant(null);
                }}
                onStatusUpdate={handleStatusUpdate}
                onApplicantUpdate={handleApplicantUpdate}
            />
        );
    }

    // If showing create campaign page, render only the create campaign page
    if (showCreateCampaign) {
        return (
            <CreateCampaignPage
                onBack={() => setShowCreateCampaign(false)}
                onCreateCampaign={handleCreateCampaign}
            />
        );
    }

    // If showing add applicant page, render only the add applicant page
    if (showAddApplicant && selectedCampaign) {
        return (
            <AddApplicantPage
                campaign={selectedCampaign}
                onBack={() => setShowAddApplicant(false)}
                onSave={handleAddApplicant}
            />
        );
    }


    // If showing fragebogen creation page, render only the fragebogen creation page
    if (showFragebogenCreation && selectedCampaign) {
        return (
            <FragebogenCreationPage
                campaign={selectedCampaign}
                onBack={() => setShowFragebogenCreation(false)}
                onSave={handleSaveFragebogen}
            />
        );
    }

    // If showing fragebogen taker page, render only the fragebogen taker page
    if (showFragebogenTaker && currentFragebogen) {
        return (
            <FragebogenTakerPage
                fragebogenData={currentFragebogen}
                onBack={() => {
                    setShowFragebogenTaker(false);
                    setCurrentFragebogen(null);
                }}
                onSubmit={handleFragebogenSubmission}
            />
        );
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
            {/* Top Header */}
            <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-6">
                <div className="flex justify-center">
                    <button
                        onClick={() => navigateTo('dashboard')}
                        className="flex items-center gap-2 text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors font-medium"
                    >
                        <ArrowLeftIcon className="w-4 h-4" />
                        Zurück zum Dashboard
                    </button>
                </div>
            </div>

                <div className="flex h-[calc(100vh-80px)]">
                {/* Campaign Sidebar */}
                <div className="w-80 bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 flex flex-col">
                    {/* Sidebar Header */}
                    <div className="p-6 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xl font-bold text-slate-800 dark:text-slate-100">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                </svg>
                                Kampagnen
                            </div>
                        </div>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Kampagnen verwalten und Bewerbungen bündeln</p>
                    </div>

                    {/* Campaign List */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-50 dark:bg-slate-900">
                        {campaigns.map((campaign) => (
                            <div
                                key={campaign.id}
                                onClick={() => handleCampaignSelect(campaign)}
                                className={`group relative p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                                    selectedCampaign?.id === campaign.id
                                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 shadow-xs'
                                        : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500 bg-white dark:bg-slate-700'
                                }`}
                            >
                                {/* Campaign Content */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                                        <div className={`w-3 h-3 rounded-full ${campaign.color} shrink-0`}></div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className={`font-semibold text-sm truncate ${
                                                selectedCampaign?.id === campaign.id
                                                    ? 'text-indigo-800 dark:text-indigo-200'
                                                    : 'text-slate-800 dark:text-slate-100'
                                            }`}>
                                                {campaign.name}
                                            </h3>
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            campaign.status === 'online' 
                                                ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300'
                                                : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300'
                                        }`}>
                                            {campaign.status === 'online' ? 'Online' : 'Offline'}
                                        </span>
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                            selectedCampaign?.id === campaign.id
                                                ? 'bg-indigo-200 text-indigo-800 dark:bg-indigo-800 dark:text-indigo-200'
                                                : 'bg-slate-100 text-slate-600 dark:bg-slate-600 dark:text-slate-300'
                                        }`}>
                                            {campaign.applicantCount}
                                        </span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteCampaign(campaign.id);
                                            }}
                                            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-all"
                                            title="Position löschen"
                                        >
                                            <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {/* Add Campaign Button */}
                        <button
                            onClick={() => setShowCreateCampaign(true)}
                            className="w-full p-3 rounded-lg border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all duration-200 flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            <span className="font-medium">Neue Kampagne</span>
                        </button>

                        {/* Empty State */}
                        {campaigns.length === 0 && (
                            <div className="text-center py-8">
                                <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-medium text-slate-600 dark:text-slate-400 mb-2">
                                    Keine Kampagnen
                                </h3>
                                <p className="text-sm text-slate-500 dark:text-slate-500 mb-4">
                                    Erstelle deine erste Kampagne
                                </p>
                                <ModernButton
                                    onClick={() => setShowCreateCampaign(true)}
                                    variant="primary"
                                    size="sm"
                                    icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
                                >
                                    Erste Kampagne erstellen
                                </ModernButton>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content */}
                    <div className="flex-1 flex flex-col overflow-hidden bg-slate-50 dark:bg-slate-900">
                    {/* Header */}
                    <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 p-6 shadow-xs">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                {selectedCampaign && (
                                    <div className={`w-4 h-4 rounded-full ${selectedCampaign.color} shadow-xs`}></div>
                                )}
                            <div>
                                    <div className="flex items-center space-x-2">
                                        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                                            {selectedCampaign ? selectedCampaign.name : 'Bewerbungspool'}
                                        </h2>
                                        {selectedCampaign && (
                                            <>
                                                <span className="text-slate-400 dark:text-slate-500">•</span>
                                                <span className="text-slate-600 dark:text-slate-400 font-medium">
                                                    {selectedCampaign.applicantCount} Bewerbungen
                                                </span>
                                                <span className="text-slate-400 dark:text-slate-500">•</span>
                                                <select
                                                    value={selectedCampaign.status}
                                                    onChange={(e) => handleStatusChange(e.target.value)}
                                                    className="px-2 py-1 text-sm border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                                                >
                                                    <option value="online">Online</option>
                                                    <option value="offline">Offline</option>
                                                </select>
                                                <span className="text-slate-400 dark:text-slate-500">•</span>
                                                <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                                    <span>Quiz-Scoring</span>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedCampaign.enable_quiz_scoring === true}
                                                        onChange={(e) => handleQuizScoringToggle(e.target.checked)}
                                                        className="toggle toggle-sm toggle-primary"
                                                    />
                                                </label>
                                            </>
                                        )}
                                    </div>
                                <div className="flex items-center space-x-4">
                                <p className="text-slate-600 dark:text-slate-400">
                                        {selectedCampaign ? '' : 'Wähle eine Position aus'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions + Filters Toolbar */}
                    <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex flex-wrap items-center gap-3">
                                {selectedApplicants.size > 0 && (
                                    <ModernButton
                                        onClick={handleBulkDelete}
                                        variant="danger"
                                        size="sm"
                                        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>}
                                    >
                                        Löschen ({selectedApplicants.size})
                                    </ModernButton>
                                )}
                                <ModernButton
                                    onClick={() => selectedCampaign && setShowAddApplicant(true)}
                                    variant="primary"
                                    size="sm"
                                    disabled={!selectedCampaign || selectedCampaign?.status === 'offline'}
                                    icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>}
                                >
                                    Eintrag hinzufügen
                                </ModernButton>

                                <div className="flex flex-wrap items-center gap-2">
                                    <SplitButton
                                        onClick={() => selectedCampaign && handleEditLandingPage()}
                                        variant="secondary"
                                        size="sm"
                                        disabled={!selectedCampaign}
                                        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>}
                                        dropdownItems={[
                                            {
                                                label: "Landing Page ansehen",
                                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>,
                                                onClick: () => selectedCampaign && handleViewLandingPage(),
                                                disabled: !selectedCampaign?.customRoute
                                            },
                                            {
                                                label: "Route bearbeiten",
                                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>,
                                                onClick: () => selectedCampaign && setShowCustomRouteModal(true),
                                                disabled: !selectedCampaign?.customRoute
                                            },
                                            {
                                                label: "URL kopieren",
                                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
                                                onClick: () => {
                                                    if (selectedCampaign?.customRoute) {
                                                        const { companyName, campaignName, landingPageName } = selectedCampaign.customRoute;
                                                        const url = getLandingPageUrl(companyName, campaignName, landingPageName);
                                                        navigator.clipboard.writeText(url);
                                                        showNotification('Landing Page URL kopiert!');
                                                    }
                                                },
                                                disabled: !selectedCampaign?.customRoute
                                            }
                                        ]}
                                    >
                                        Landing Page erstellen/bearbeiten
                                    </SplitButton>

                                    <SplitButton
                                        onClick={handleCreateFragebogen}
                                        variant="secondary"
                                        size="sm"
                                        disabled={!selectedCampaign}
                                        icon={<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                                        dropdownItems={[
                                            {
                                                label: "Quiz ansehen",
                                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>,
                                                onClick: () => selectedCampaign && handleTakeFragebogen(),
                                                disabled: !selectedCampaign || !fragebogenService.hasFragebogen(selectedCampaign?.id)
                                            },
                                            {
                                                label: "URL kopieren",
                                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>,
                                                onClick: () => {
                                                    if (selectedCampaign && fragebogenService.hasFragebogen(selectedCampaign.id)) {
                                                        const fragebogenUrl = getFragebogenUrl(selectedCampaign.id);
                                                        navigator.clipboard.writeText(fragebogenUrl);
                                                        showNotification('Quiz-URL kopiert!');
                                                    }
                                                },
                                                disabled: !selectedCampaign || !fragebogenService.hasFragebogen(selectedCampaign?.id)
                                            },
                                            {
                                                label: "QR-Code anzeigen",
                                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>,
                                                onClick: () => {
                                                    if (selectedCampaign) {
                                                        setQrModalUrl(getFragebogenUrl(selectedCampaign.id));
                                                        setShowQrModal(true);
                                                    }
                                                },
                                                disabled: !selectedCampaign
                                            }
                                        ]}
                                    >
                                        Quiz erstellen
                                    </SplitButton>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <div className="w-64">
                                    <input
                                        type="text"
                                        placeholder="Suchen..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    />
                                </div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    {statusOptions.map(status => (
                                        <option key={status} value={status}>{status}</option>
                                    ))}
                                </select>
                                <select
                                    value={sortField}
                                    onChange={(e) => setSortField(e.target.value)}
                                    className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                >
                                    <option value="date">Datum</option>
                                    <option value="name">Name</option>
                                    <option value="status">Status</option>
                                </select>
                                <button
                                    onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                                    className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                                >
                                    {sortDirection === 'asc' ? (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    )}
                                </button>
                                <button
                                    onClick={loadData}
                                    disabled={isLoading}
                                    className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Aktualisieren"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                        <span className="ml-2 text-slate-600 dark:text-slate-400">Lade Bewerbungen...</span>
                    </div>
                        ) : !selectedCampaign ? (
                            <div className="text-center py-12">
                                <UserGroupIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                                <h3 className="text-xl font-medium text-slate-600 dark:text-slate-400 mb-2">
                                    Wähle eine Kampagne aus
                                </h3>
                                <p className="text-slate-500 dark:text-slate-500">
                                    Klicke auf eine Kampagne in der Seitenleiste, um die Bewerbungen anzuzeigen
                                </p>
                    </div>
                ) : filteredApplicants.length === 0 ? (
                    <div className="text-center py-12">
                        <UserGroupIcon className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                        <h3 className="text-xl font-medium text-slate-600 dark:text-slate-400 mb-2">
                            {applicants.length === 0 ? 'Keine Bewerbungen vorhanden' : 'Keine Ergebnisse gefunden'}
                        </h3>
                        <p className="text-slate-500 dark:text-slate-500">
                            {applicants.length === 0 
                                ? 'Neue Bewerbungen erscheinen hier automatisch' 
                                : 'Versuche andere Suchkriterien'}
                        </p>
                    </div>
                ) : (
                    <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[760px]">
                                <thead className="bg-slate-100 dark:bg-slate-700 border-b border-slate-300 dark:border-slate-600">
                                    <tr>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider w-12">
                                            <input
                                                type="checkbox"
                                                checked={selectedApplicants.size === filteredApplicants.length && filteredApplicants.length > 0}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                            />
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            E-Mail
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            Telefon
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            Erstellt
                                        </th>
                                        <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                            Letzte Änderung
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                                    {filteredApplicants.map((applicant) => (
                                        <tr 
                                            key={applicant.leadID}
                                            className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                                        >
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedApplicants.has(applicant.leadID)}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectApplicant(applicant.leadID, e.target.checked);
                                                    }}
                                                    className="w-4 h-4 text-indigo-600 bg-gray-100 border-gray-300 rounded focus:ring-indigo-500 dark:focus:ring-indigo-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                                                />
                                            </td>
                                            <td 
                                                className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors duration-200"
                                                onClick={() => handleApplicantClick(applicant)}
                                            >
                                                <div className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200 flex items-center gap-2">
                                                    {isNewApplicant(applicant) && <NeuBadge />}
                                                    <span>{applicant.name}</span>
                                                    <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                    </svg>
                                                </div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                                <div className="text-sm text-slate-900 dark:text-slate-100">{applicant.email}</div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                                <div className="text-sm text-slate-900 dark:text-slate-100">{applicant.phone || '-'}</div>
                                            </td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                                                <StatusBadge 
                                                    status={applicant.status}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleApplicantClick(applicant);
                                                    }}
                                                />
                                            </td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                                {new Date(applicant.createdAt).toLocaleDateString('de-DE')}
                                            </td>
                                            <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">
                                                {new Date(applicant.lastChange).toLocaleDateString('de-DE')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                    </div>
                </div>
            </div>
            
            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && campaignToDelete && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 z-50">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl max-w-md w-full m-4 transform transition-all duration-300">
                        <div className="flex items-center gap-3 mb-4 p-6 pb-0">
                            <div className="shrink-0">
                                <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100">Position löschen?</h2>
                        </div>
                        <div className="px-6 pb-6">
                            <p className="text-slate-600 dark:text-slate-300 mb-6">
                                Möchten Sie die Position <strong>"{campaignToDelete.name}"</strong> wirklich löschen?
                                <br /><br />
                                Alle zugehörigen Bewerbungen ({campaignToDelete.applicantCount}) gehen unwiderruflich verloren.
                            </p>
                            <div className="flex gap-3">
                                <ModernButton 
                                    onClick={cancelDeleteCampaign} 
                                    variant="secondary" 
                                    size="lg" 
                                    className="flex-1"
                                >
                                    Abbrechen
                                </ModernButton>
                                <ModernButton 
                                    onClick={confirmDeleteCampaign} 
                                    variant="danger" 
                                    size="lg" 
                                    className="flex-1"
                                >
                                    Löschen
                                </ModernButton>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Route Modal */}
            <CustomRouteModal
                isOpen={showCustomRouteModal}
                onClose={() => setShowCustomRouteModal(false)}
                onConfirm={handleCustomRouteConfirm}
                existingRoutes={getExistingRoutes()}
                initialData={selectedCampaign?.customRoute || null}
                campaignName={selectedCampaign?.name || ''}
            />

            {/* QR Code Modal for Apply URL (store / in-person scenario) */}
            {showQrModal && qrModalUrl && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
                    onClick={(e) => { if (e.target === e.currentTarget) { setShowQrModal(false); setQrModalUrl(''); } }}
                >
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-bold text-lg text-slate-800 dark:text-slate-100">QR-Code</h3>
                                <button
                                    type="button"
                                    onClick={() => { setShowQrModal(false); setQrModalUrl(''); }}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1"
                                    aria-label="Schließen"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                            <div className="flex flex-col items-center gap-4 py-2">
                                <img
                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(qrModalUrl)}`}
                                    alt="QR-Code zur Bewerbung"
                                    className="w-56 h-56 rounded-lg border border-slate-200 dark:border-slate-600"
                                />
                                <div className="w-full">
                                    <input
                                        type="text"
                                        readOnly
                                        value={qrModalUrl}
                                        className="w-full px-3 py-2 text-xs font-mono border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
                                        onClick={(e) => e.target.select()}
                                    />
                                </div>
                            </div>
                            <div className="flex flex-wrap justify-end gap-2 mt-6 min-w-0">
                                <button
                                    type="button"
                                    disabled={qrDownloading}
                                    className="inline-flex items-center justify-center gap-1.5 flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 text-sm whitespace-nowrap"
                                    onClick={async () => {
                                        setQrDownloading(true);
                                        try {
                                            const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(qrModalUrl)}`;
                                            const res = await fetch(qrSrc);
                                            const blob = await res.blob();
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = 'qr-code-bewerbung.png';
                                            a.click();
                                            URL.revokeObjectURL(url);
                                            showNotification('QR-Code heruntergeladen');
                                        } catch (err) {
                                            console.error('QR-Download fehlgeschlagen:', err);
                                            showNotification('Download fehlgeschlagen', 'error');
                                        } finally {
                                            setQrDownloading(false);
                                        }
                                    }}
                                >
                                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                                    </svg>
                                    <span className="truncate">{qrDownloading ? '…' : 'Herunterladen'}</span>
                                </button>
                                <button
                                    type="button"
                                    className="inline-flex items-center justify-center gap-1.5 flex-1 min-w-0 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm whitespace-nowrap"
                                    onClick={() => {
                                        navigator.clipboard.writeText(qrModalUrl);
                                        showNotification('URL kopiert!');
                                    }}
                                >
                                    <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    <span className="truncate">Kopieren</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Notification */}
            {notification && (
                <Notification
                    message={notification.message}
                    type={notification.type}
                    onClose={() => setNotification(null)}
                />
            )}
        </div>
    );
}
